import React, { useState, useEffect } from 'react';
import { clustersAPI } from '../services/api';
import { useNamespaces } from '../pages/LivePage';
import './DeployImageModal.css';

const DEFAULT_FORM = {
  name: '', namespace: 'default', image: '', replicas: 1,
  env: [], ports: [], volumes: [],
  cpu_request: '100m', memory_request: '128Mi',
  cpu_limit: '500m', memory_limit: '512Mi',
};

// Matches OpenShift's `oc set volume --add -t` type set, minus hostPath (not exposed
// anywhere else in this codebase's user-facing deploy surface, for the same security
// reason — see app_services_manifest.py's extra_volumes). `resource` is the Steve-paged
// endpoint name; `dataKey` is that response's own items key — same per-resource
// abbreviation each page already uses (Secrets.js/ConfigMaps.js/PVCs.js), not a
// generic `data[resource]` lookup, since PVCs' key is "pvcs", not "persistentvolumeclaims".
const VOLUME_TYPES = [
  { value: 'configMap', label: 'ConfigMap', resource: 'configmaps', dataKey: 'configmaps' },
  { value: 'secret', label: 'Secret', resource: 'secrets', dataKey: 'secrets' },
  { value: 'persistentVolumeClaim', label: 'PVC', resource: 'persistentvolumeclaims', dataKey: 'pvcs' },
  { value: 'emptyDir', label: 'Empty Dir (scratch space)', resource: null, dataKey: null },
];

export default function DeployImageModal({ clusterId, onClose, onDeployed }) {
  const [form, setForm]     = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState(null);
  const { namespaces } = useNamespaces(clusterId);
  const [creatingNs, setCreatingNs] = useState(false);
  // Separate option lists per resource kind, keyed by resource name (configmaps/secrets/
  // persistentvolumeclaims) — refetched whenever the namespace changes, since each kind's
  // existing objects are namespace-scoped.
  const [volumeOptions, setVolumeOptions] = useState({});

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!clusterId || !form.namespace) return;
    let cancelled = false;
    const kinds = VOLUME_TYPES.filter(t => t.resource);
    Promise.all(kinds.map(t =>
      clustersAPI.getWorkloadPaged(t.resource, clusterId, { pageSize: 500, namespace: form.namespace })
        .then(({ data }) => [t.resource, (data[t.dataKey] || data.items || []).map(it => it.metadata?.name || it.name).filter(Boolean)])
        .catch(() => [t.resource, []])
    )).then(pairs => { if (!cancelled) setVolumeOptions(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [clusterId, form.namespace]);

  // Once the namespace list loads, if 'default' isn't actually one of this
  // cluster's namespaces, fall back to the first real one instead of silently
  // submitting a namespace that doesn't exist.
  useEffect(() => {
    if (namespaces.length && form.namespace === 'default' && !namespaces.includes('default')) {
      set('namespace', namespaces[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespaces]);

  const addEnv  = () => set('env',   [...form.env,   { name: '', value: '' }]);
  const addPort = () => set('ports', [...form.ports, '']);
  const addVolume = () => set('volumes', [...form.volumes, { type: 'configMap', name_ref: '', mount_path: '', sub_path: '' }]);
  const setVolume = (i, field, val) => {
    const next = [...form.volumes];
    next[i] = { ...next[i], [field]: val };
    if (field === 'type') next[i].name_ref = '';  // options differ per type, stale pick would be wrong
    set('volumes', next);
  };
  const removeVolume = (i) => set('volumes', form.volumes.filter((_, j) => j !== i));

  const setEnv = (i, field, val) => {
    const next = [...form.env];
    next[i] = { ...next[i], [field]: val };
    set('env', next);
  };
  const removeEnv  = (i) => set('env',   form.env.filter((_, j) => j !== i));
  const setPort    = (i, val) => { const next = [...form.ports]; next[i] = val; set('ports', next); };
  const removePort = (i) => set('ports', form.ports.filter((_, j) => j !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.image.trim()) {
      setError('Name and image are required.');
      return;
    }
    if (!form.namespace.trim()) {
      setError('Namespace is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        replicas: parseInt(form.replicas, 10) || 1,
        ports: form.ports.filter(p => p).map(Number),
        env: form.env.filter(e => e.name.trim()),
        // A row needs a mount path always, and a source name unless it's emptyDir
        // (which has none to pick) — drop anything left incomplete rather than send
        // a volume the backend would just skip anyway.
        volumes: form.volumes.filter(v => v.mount_path.trim() && (v.type === 'emptyDir' || v.name_ref.trim())),
      };
      const { data } = await clustersAPI.deployImage(clusterId, payload);
      setSuccess(data);
      if (onDeployed) onDeployed(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Deploy failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="deploy-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="deploy-modal">
          <div className="deploy-success">
            <div className="deploy-success-icon">✓</div>
            <h3>Deployment {success.action}!</h3>
            <p><strong>{success.name}</strong> in <code>{success.namespace}</code></p>
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>Image: {success.image} · {success.replicas} replica(s)</p>
            <div className="deploy-success-actions">
              <button className="button-primary" onClick={onClose}>Done</button>
              <button className="button-secondary" onClick={() => { setSuccess(null); setForm(DEFAULT_FORM); }}>Deploy Another</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="deploy-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="deploy-modal">
        <div className="deploy-modal-header">
          <h2>🚀 Deploy Image</h2>
          <button className="deploy-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="deploy-form">
          {/* Basic */}
          <div className="deploy-section">
            <div className="deploy-row">
              <div className="deploy-field">
                <label>Deployment Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)}
                  placeholder="my-app" required />
              </div>
              <div className="deploy-field">
                <label>Namespace</label>
                {creatingNs || namespaces.length === 0 ? (
                  <input value={form.namespace} onChange={e => set('namespace', e.target.value)}
                    placeholder="default" autoFocus={creatingNs} />
                ) : (
                  <select
                    value={namespaces.includes(form.namespace) ? form.namespace : ''}
                    onChange={e => {
                      if (e.target.value === '__new__') { setCreatingNs(true); set('namespace', ''); }
                      else set('namespace', e.target.value);
                    }}
                  >
                    {!namespaces.includes(form.namespace) && <option value="" disabled>Select a namespace…</option>}
                    {namespaces.map(n => <option key={n} value={n}>{n}</option>)}
                    <option value="__new__">+ New namespace…</option>
                  </select>
                )}
                {creatingNs && (
                  <button type="button" className="deploy-add-btn" style={{ marginTop: 4 }}
                    onClick={() => { setCreatingNs(false); set('namespace', namespaces.includes('default') ? 'default' : (namespaces[0] || '')); }}>
                    ← choose existing namespace
                  </button>
                )}
              </div>
            </div>
            <div className="deploy-row">
              <div className="deploy-field deploy-field-wide">
                <label>Container Image *</label>
                <input value={form.image} onChange={e => set('image', e.target.value)}
                  placeholder="nginx:latest or registry.sarv.com/app:v1.2" required />
              </div>
              <div className="deploy-field deploy-field-narrow">
                <label>Replicas</label>
                <input type="number" min={1} max={50} value={form.replicas}
                  onChange={e => set('replicas', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Resources */}
          <div className="deploy-section">
            <div className="deploy-section-title">Resource Limits</div>
            <div className="deploy-row">
              <div className="deploy-field"><label>CPU Request</label>
                <input value={form.cpu_request} onChange={e => set('cpu_request', e.target.value)} placeholder="100m" /></div>
              <div className="deploy-field"><label>Memory Request</label>
                <input value={form.memory_request} onChange={e => set('memory_request', e.target.value)} placeholder="128Mi" /></div>
              <div className="deploy-field"><label>CPU Limit</label>
                <input value={form.cpu_limit} onChange={e => set('cpu_limit', e.target.value)} placeholder="500m" /></div>
              <div className="deploy-field"><label>Memory Limit</label>
                <input value={form.memory_limit} onChange={e => set('memory_limit', e.target.value)} placeholder="512Mi" /></div>
            </div>
          </div>

          {/* Ports */}
          <div className="deploy-section">
            <div className="deploy-section-title">
              Ports
              <button type="button" className="deploy-add-btn" onClick={addPort}>+ Add Port</button>
            </div>
            {form.ports.map((p, i) => (
              <div key={i} className="deploy-kv-row">
                <input type="number" value={p} onChange={e => setPort(i, e.target.value)} placeholder="8080" style={{ width: 120 }} />
                <button type="button" className="deploy-remove-btn" onClick={() => removePort(i)}>✕</button>
              </div>
            ))}
          </div>

          {/* Env vars */}
          <div className="deploy-section">
            <div className="deploy-section-title">
              Environment Variables
              <button type="button" className="deploy-add-btn" onClick={addEnv}>+ Add Variable</button>
            </div>
            {form.env.map((e, i) => (
              <div key={i} className="deploy-kv-row">
                <input value={e.name} onChange={ev => setEnv(i, 'name', ev.target.value)} placeholder="KEY" />
                <span>=</span>
                <input value={e.value} onChange={ev => setEnv(i, 'value', ev.target.value)} placeholder="value" />
                <button type="button" className="deploy-remove-btn" onClick={() => removeEnv(i)}>✕</button>
              </div>
            ))}
          </div>

          {/* Volumes */}
          <div className="deploy-section">
            <div className="deploy-section-title">
              Volumes
              <button type="button" className="deploy-add-btn" onClick={addVolume}>+ Add Volume</button>
            </div>
            {form.volumes.map((v, i) => {
              const typeDef = VOLUME_TYPES.find(t => t.value === v.type);
              const options = typeDef?.resource ? (volumeOptions[typeDef.resource] || []) : [];
              return (
                <div key={i} className="deploy-kv-row" style={{ alignItems: 'center' }}>
                  <select value={v.type} onChange={e => setVolume(i, 'type', e.target.value)} style={{ width: 150 }}>
                    {VOLUME_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {typeDef?.resource ? (
                    <select value={v.name_ref} onChange={e => setVolume(i, 'name_ref', e.target.value)} style={{ width: 160 }}>
                      <option value="" disabled>{options.length ? 'Select…' : `(no ${typeDef.label}s in this namespace)`}</option>
                      {options.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  ) : (
                    <span style={{ width: 160, color: 'var(--muted)', fontSize: 12 }}>ephemeral, no source</span>
                  )}
                  <input value={v.mount_path} onChange={e => setVolume(i, 'mount_path', e.target.value)}
                    placeholder="/etc/config" style={{ width: 150 }} />
                  <button type="button" className="deploy-remove-btn" onClick={() => removeVolume(i)}>✕</button>
                </div>
              );
            })}
          </div>

          {error && <div className="deploy-error">{error}</div>}

          <div className="deploy-actions">
            <button type="button" className="button-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="button-primary" disabled={loading}>
              {loading ? 'Deploying...' : '🚀 Deploy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
